import { createLocalStorageAdapter, } from "@vninvestcharts/engine-drawing";
import { useCallback, useEffect, useMemo, useRef } from "react";
/**
 * P1-10: drawings scoped to another symbol/timeframe must never leak into this
 * key. Unscoped drawings (no symbol/timeframe set) always pass through.
 */
export function filterDrawingsByScope(drawings, symbol, timeframe) {
    return drawings.filter((drawing) => ((!drawing.symbol || drawing.symbol === symbol)
        && (!drawing.timeframe || drawing.timeframe === timeframe)));
}
export function useDrawingStorage(symbol, timeframe, drawings, onLoad, adapter, options) {
    const storage = useMemo(() => adapter ?? createLocalStorageAdapter(), [adapter]);
    const hydratedRef = useRef(false);
    const loadGenerationRef = useRef(null);
    const isEnabled = options?.enabled ?? true;
    const onStorageErrorRef = useRef(options?.onStorageError);
    onStorageErrorRef.current = options?.onStorageError;
    // Latest committed `drawings` (render-body mirror, same pattern as
    // onStorageErrorRef above). Read by accept() below so the snapshot it
    // records is always the render that was current when the load landed.
    const drawingsRef = useRef(drawings);
    drawingsRef.current = drawings;
    // StrictMode P0 (2026-10-12): the `drawings` array of the render in which
    // the most recent accept() ran, plus whether that load carried real data.
    // A SAVE-setup whose own `drawings` is the SAME reference is a pre-accept
    // render: its payload predates the load, so queueing it would let the
    // effect cleanup flush stale (often empty) data over real stored drawings
    // (StrictMode unmount runs that cleanup while hydratedRef is already true).
    // Skip the queue in that case — but ONLY when the load was non-empty: over
    // empty storage there is nothing to clobber, and mount-time drawings win
    // (locked by useDrawingStorage.debounce.test.tsx (b)). The render that
    // accept()'s dispatch produces always carries a new reference and queues
    // fresh data; a bailed-out reconcile means storage already matches state,
    // so skipping loses nothing. In production the controller ALWAYS
    // dispatches on load (reconcile/resetScope both return new state), so the
    // skip never stands past the post-accept render there.
    const lastAcceptRef = useRef(null);
    const notifyError = useCallback((operation, error) => {
        console.warn(`Failed to ${operation} drawings:`, error);
        onStorageErrorRef.current?.({ operation, error });
    }, []);
    useEffect(() => {
        if (!isEnabled) {
            hydratedRef.current = false;
            return;
        }
        hydratedRef.current = false;
        // P1: generation token — a slow load for a previous symbol/timeframe must
        // never overwrite the drawings of the current one (load race).
        const generation = Symbol("drawings-load");
        loadGenerationRef.current = generation;
        const loaded = storage.load(symbol, timeframe);
        const accept = (next) => {
            if (loadGenerationRef.current !== generation)
                return;
            lastAcceptRef.current = { drawings: drawingsRef.current, nonEmpty: next.length > 0 };
            onLoad(next);
            hydratedRef.current = true;
        };
        if (loaded instanceof Promise) {
            loaded.then(accept).catch((err) => {
                if (loadGenerationRef.current !== generation)
                    return;
                notifyError("load", err);
                lastAcceptRef.current = { drawings: drawingsRef.current, nonEmpty: false };
                onLoad([]);
                hydratedRef.current = true;
            });
        }
        else {
            accept(loaded);
        }
    }, [isEnabled, notifyError, onLoad, storage, symbol, timeframe]);
    const pendingSaveRef = useRef(null);
    const flushPendingSave = useCallback(() => {
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        if (!pending)
            return;
        try {
            const saver = storage.saveWithResult?.bind(storage) ?? storage.save.bind(storage);
            const result = saver(pending.symbol, pending.timeframe, pending.drawings);
            if (result instanceof Promise) {
                result.then((ok) => {
                    if (ok === false)
                        notifyError("save", new Error("Drawing storage reported failure"));
                }, (err) => notifyError("save", err));
            }
            else if (result === false) {
                notifyError("save", new Error("Drawing storage reported failure"));
            }
        }
        catch (err) {
            notifyError("save", err);
        }
    }, [notifyError, storage]);
    useEffect(() => {
        if (!isEnabled) {
            return;
        }
        if (!hydratedRef.current) {
            return;
        }
        // StrictMode P0: this render predates the load that just landed (same
        // reference as the accept snapshot) AND the load carried real data.
        // Its `drawings` are pre-hydration data: do NOT queue them, or the
        // cleanup below would flush them over the real stored drawings. The
        // post-accept render queues fresh data. A skipped commit never leaves
        // a flushable payload behind.
        if (lastAcceptRef.current !== null
            && lastAcceptRef.current.nonEmpty
            && lastAcceptRef.current.drawings === drawings) {
            pendingSaveRef.current = null;
            return;
        }
        // P1-10: persist only unscoped + same-scope drawings under this key.
        pendingSaveRef.current = { symbol, timeframe, drawings: filterDrawingsByScope(drawings, symbol, timeframe) };
        const timeoutId = globalThis.setTimeout(flushPendingSave, 300);
        return () => {
            globalThis.clearTimeout(timeoutId);
            // P1-04: flush synchronously on unmount/symbol-switch so strokes made
            // in the last 300ms are not lost. Hydrated-only: never persist the
            // pre-load empty state over real stored drawings.
            if (hydratedRef.current) {
                flushPendingSave();
            }
            else {
                pendingSaveRef.current = null;
            }
        };
    }, [drawings, flushPendingSave, isEnabled, storage, symbol, timeframe]);
    const exportJSON = useCallback(() => {
        const payload = storage.exportJSON(drawings);
        if (typeof document === "undefined") {
            return payload;
        }
        const blob = new Blob([payload], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "drawings.json";
        anchor.click();
        URL.revokeObjectURL(url);
        return payload;
    }, [drawings, storage]);
    const importJSON = useCallback(async (file) => {
        const payload = await file.text();
        // P1-05: the sync path used to let DrawingImportError escape into the UI
        // event handler unhandled. Surface it via onStorageError, then rethrow so
        // existing control flow is preserved.
        let imported;
        try {
            imported = storage.importJSON(payload);
        }
        catch (err) {
            notifyError("import", err);
            throw err;
        }
        if (imported instanceof Promise) {
            try {
                const resolved = await imported;
                onLoad(resolved);
            }
            catch (err) {
                notifyError("import", err);
            }
        }
        else {
            onLoad(imported);
        }
    }, [notifyError, onLoad, storage]);
    const clearAll = useCallback(() => {
        const p = storage.clear(symbol, timeframe);
        if (p instanceof Promise) {
            p.catch((err) => {
                console.warn("Failed to clear drawings:", err);
            });
        }
        onLoad([]);
    }, [onLoad, storage, symbol, timeframe]);
    // CE25: Multi-timeframe persistence (legacy `-all` read-cache only).
    // P1/D-09: the `saveAllForSymbol` writer is removed — per-symbol+timeframe
    // keys are the single source of truth.
    const loadAllForSymbol = useCallback(async (targetSymbol) => {
        const loaded = storage.loadAllForSymbol(targetSymbol);
        if (loaded instanceof Promise) {
            try {
                const resolved = await loaded;
                onLoad(resolved);
            }
            catch (err) {
                notifyError("load", err);
            }
        }
        else {
            onLoad(loaded);
        }
    }, [notifyError, onLoad, storage]);
    return useMemo(() => ({ exportJSON, importJSON, clearAll, loadAllForSymbol }), [clearAll, exportJSON, importJSON, loadAllForSymbol]);
}
